import React, { createContext, useContext, useState, useEffect } from 'react'

type SubCart = {
  id: null | number;
  user: { [key: string]: any }
}

type ConsolidateData = {
  mainFboId?: string
  mainCartId?: number
  subCart: SubCart
} | null

type ConsolidateContextValue = {
  consolidateList: ConsolidateData[];
  activeSubCartId: number | null;
  addConsolidate: (data: ConsolidateData) => void;
  setActiveSubCart: (id: number | null) => void;
  removeConsolidate: (subCartId: number | null) => void;
  clearConsolidates: () => void;
}

const ConsolidateContext = createContext<ConsolidateContextValue>({
  consolidateList: [],
  activeSubCartId: null,
  addConsolidate: () => {},
  setActiveSubCart: () => {},
  removeConsolidate: () => {},
  clearConsolidates: () => {},
})

export const ConsolidateProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [consolidateList, setConsolidateList] = useState<ConsolidateData[]>([])

  const [activeSubCartId, setActiveSubCartId] = useState<number | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('activeSubCartId')
      return stored ? Number(stored) : null
    }
    return null
  })

  const setActiveSubCart = (id: number | null) => {
    setActiveSubCartId(id)
    if (typeof window !== 'undefined') {
      if (id !== null) {
        localStorage.setItem('activeSubCartId', id.toString())
      } else {
        localStorage.removeItem('activeSubCartId')
      }
    }
  }

  useEffect(() => {
    const json = localStorage.getItem('consolidateUsers')
    if (json) {
      try {
        setConsolidateList(JSON.parse(json) as ConsolidateData[])
      } catch (error) {
        console.error('Error parsing consolidateUsers:', error)
      }
    }
  }, [])

  const addConsolidate = (data: ConsolidateData) => {
    if (!data) return
    setConsolidateList(prev => {
      if (!prev.some(item => item?.subCart?.id === data.subCart?.id)) {
        const newList = [...prev, data]
        localStorage.setItem('consolidateUsers', JSON.stringify(newList))
        return newList
      }
      return prev
    })
  }

  const removeConsolidate = (subCartId: number | null) => {
    setConsolidateList(prev => {
      const newList = prev.filter(item => item?.subCart?.id !== subCartId)
      localStorage.setItem('consolidateUsers', JSON.stringify(newList))
      if (activeSubCartId === subCartId) setActiveSubCartId(null)
      return newList
    })
  }

  const clearConsolidates = () => {
    localStorage.removeItem('consolidateUsers')
    localStorage.removeItem('activeSubCartId')
    setActiveSubCartId(null)
    setConsolidateList([])
  }

  return React.createElement(
    ConsolidateContext.Provider,
    {
      value: {
        consolidateList,
        activeSubCartId,
        addConsolidate,
        setActiveSubCart,
        removeConsolidate,
        clearConsolidates,
      },
    },
    children
  )
}

export const useConsolidate = () => useContext(ConsolidateContext)